import {
  memo,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import styled from "@emotion/styled";
import {
  text,
  surface,
  border,
  spacing,
  controlFontSize,
  fontFamily,
  fontWeight,
  borderRadius,
  shadow,
  layout,
  zIndex,
  duration,
  opacity,
  lineHeight,
  focusRing,
} from "@atlas-int/tokens";

// ─── Public types ─────────────────────────────────────────────────────────────

export interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  data?: unknown;
  [key: string]: unknown;
}

export interface FlatItem {
  id: string;
  [key: string]: unknown;
}

export interface TreeSelectItemRenderProps<T> {
  node: TreeNode;
  item: T;
  style: React.CSSProperties;
  isExpanded: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  hasChildren: boolean;
  level: number;
  onToggle: () => void;
  onClick: () => void;
}

export interface TreeSelectProps<T extends FlatItem> {
  /** Flat array of items. */
  items: T[];
  /** Builds a hierarchical tree from the flat items. */
  buildTree: (items: T[]) => TreeNode[];
  /** Currently selected item (controlled). */
  value?: T;
  /** Fires when the user selects a leaf/item. */
  onChange: (item: T) => void;
  /** Placeholder shown when nothing is selected and the input is empty. */
  placeholder?: string;
  /**
   * Accessible name for the combobox input. A placeholder is not a reliable
   * accessible name, so screen-reader users need this to know what the
   * control selects (e.g. "Exporter", "Product").
   */
  ariaLabel?: string;
  /** Returns the display label for a given item. */
  getLabel: (item: T) => string;
  /** Predicate used for search filtering. */
  searchFilter: (item: T, term: string) => boolean;
  /** Custom renderer for each visible row. */
  renderItem: (props: TreeSelectItemRenderProps<T>) => React.ReactNode;
  /** Estimated pixel height of a single row (for virtualizer). Default: 34 */
  estimatedItemHeight?: number;
  /** Node IDs that should be expanded on mount / when value changes. */
  defaultExpandedIds?: Set<string>;
  /**
   * When searching, whether to keep matching items within their
   * parent hierarchy (true) or flatten them to a single level (false).
   * Default: true.
   */
  showParentHierarchyOnSearch?: boolean;
  /** Max height of the dropdown panel. Default: uses layout.menuMaxHeight */
  maxHeight?: string;
  /** Min width of the dropdown panel (can exceed the trigger width). */
  minWidth?: string;
  /** Disable the entire control. */
  disabled?: boolean;
  /** Additional className on the root wrapper. */
  className?: string;
}

// ─── Shared design-system primitives ─────────────────────────────────────────

import { ChevronDown, ChevronUp } from "./ChevronIcons";
import DropdownToggleButton from "./ToggleButton";

// ─── Styled primitives ────────────────────────────────────────────────────────

const Root = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  height: ${layout.controlsHeight};
  padding: 0 ${spacing[2]};
  border: 1px solid ${border.control};
  border-radius: ${borderRadius.base};
  background: ${surface.input};
  transition: border-color ${duration.fast};
  box-sizing: border-box;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? opacity.muted : 1)};

  &:hover:not([data-disabled]) {
    border-color: ${text.primary};
  }

  &:focus-within {
    border-color: ${focusRing.color};
  }
`;

const TriggerInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${fontFamily.base};
  font-weight: ${fontWeight.normal};
  font-size: ${controlFontSize.default};
  line-height: ${lineHeight.none};
  color: ${text.primary};
  padding: 0 ${spacing[4]};

  &::placeholder {
    color: ${text.muted};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ToggleButton = DropdownToggleButton;

const Panel = styled.div<{ $maxHeight?: string }>`
  position: absolute;
  z-index: ${zIndex.dropdown};
  width: 100%;
  max-height: ${({ $maxHeight }) => $maxHeight || layout.menuMaxHeight};
  overflow: hidden;
  border: 1px solid ${border.control};
  box-shadow: ${shadow.sm};
  border-radius: ${borderRadius.base};
  margin-top: ${spacing[1]};
  display: flex;
  flex-direction: column;
  background: ${surface.raised};
  box-sizing: border-box;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: auto;
  overscroll-behavior: contain;
  background: ${surface.raised};
  box-sizing: border-box;

  /* Thin custom scrollbar — consistent across platforms */
  scrollbar-width: thin;
  scrollbar-color: ${border.control} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${border.control};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${text.muted};
  }
`;

// ─── Debounce helper (avoids lodash dep) ──────────────────────────────────────

function useDebouncedCallback<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number,
): T {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return useCallback(
    ((...args: unknown[]) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    }) as T,
    [delay],
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function TreeSelectInner<T extends FlatItem>({
  items,
  buildTree,
  value,
  onChange,
  placeholder = "Search…",
  ariaLabel,
  getLabel,
  searchFilter,
  renderItem,
  estimatedItemHeight = 34,
  defaultExpandedIds,
  showParentHierarchyOnSearch = true,
  maxHeight,
  minWidth,
  disabled = false,
  className,
}: TreeSelectProps<T>) {
  // ── State ─────────────────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => defaultExpandedIds || new Set(),
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastScrollWasKeyboard = useRef(false);

  // ── Sync defaultExpandedIds when value changes while closed ───────────
  useEffect(() => {
    if (defaultExpandedIds && !isOpen) {
      setExpandedIds(defaultExpandedIds);
    }
  }, [defaultExpandedIds, isOpen]);

  // ── Tree data ─────────────────────────────────────────────────────────────
  const treeData = useMemo(() => buildTree(items), [items, buildTree]);

  const itemsById = useMemo(() => {
    const map = new Map<string, T>();
    for (const item of items) map.set(item.id, item);
    return map;
  }, [items]);

  // ── Flatten visible items ─────────────────────────────────────────────────
  const visibleItems = useMemo(() => {
    type Row = { node: TreeNode; level: number };

    const flatten = (nodes: TreeNode[], level = 0): Row[] => {
      const result: Row[] = [];
      for (const node of nodes) {
        result.push({ node, level });
        if (expandedIds.has(node.id) && node.children?.length) {
          result.push(...flatten(node.children, level + 1));
        }
      }
      return result;
    };

    if (!searchTerm) return flatten(treeData);

    const matching = items.filter((item) =>
      searchFilter(item, searchTerm.toLowerCase()),
    );

    if (showParentHierarchyOnSearch) {
      // Collect matching IDs + all ancestor IDs
      const relevantIds = new Set<string>();

      const findAncestors = (
        nodes: TreeNode[],
        targetId: string,
        path: string[] = [],
      ): string[] | null => {
        for (const node of nodes) {
          if (node.id === targetId) return path;
          if (node.children) {
            const found = findAncestors(node.children, targetId, [
              ...path,
              node.id,
            ]);
            if (found) return found;
          }
        }
        return null;
      };

      for (const item of matching) {
        relevantIds.add(item.id);
        const ancestors = findAncestors(treeData, item.id);
        if (ancestors) {
          for (const id of ancestors) relevantIds.add(id);
        }
      }

      const flattenFiltered = (nodes: TreeNode[], level = 0): Row[] => {
        const result: Row[] = [];
        for (const node of nodes) {
          if (!relevantIds.has(node.id)) continue;
          result.push({ node, level });
          if (node.children?.length) {
            result.push(...flattenFiltered(node.children, level + 1));
          }
        }
        return result;
      };
      return flattenFiltered(treeData);
    }

    // Flat search: only direct matches, no hierarchy
    return matching
      .map((item) => {
        const findNode = (nodes: TreeNode[]): TreeNode | null => {
          for (const n of nodes) {
            if (n.id === item.id) return n;
            if (n.children) {
              const found = findNode(n.children);
              if (found) return found;
            }
          }
          return null;
        };
        const node = findNode(treeData);
        return node
          ? { node, level: 0 }
          : {
              node: {
                id: item.id,
                name: (item.name_short_en as string) || item.id,
                data: item,
              },
              level: 0,
            };
      })
      .filter((r): r is Row => r !== null);
  }, [treeData, expandedIds, searchTerm, items, searchFilter, showParentHierarchyOnSearch]);

  // ── Separate sticky / scrollable ──────────────────────────────────────────
  const { stickyItems, scrollableItems } = useMemo(() => {
    const sticky: typeof visibleItems = [];
    const scrollable: typeof visibleItems = [];
    for (const row of visibleItems) {
      const orig = itemsById.get(row.node.id);
      if (orig && "isSticky" in orig && orig.isSticky) {
        sticky.push(row);
      } else {
        scrollable.push(row);
      }
    }
    return { stickyItems: sticky, scrollableItems: scrollable };
  }, [visibleItems, itemsById]);

  // ── Virtualizer ───────────────────────────────────────────────────────────
  const rowVirtualizer = useVirtualizer({
    count: scrollableItems.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => estimatedItemHeight,
    overscan: 5,
    getItemKey: (index) => scrollableItems[index]?.node?.id || index,
    initialRect: { width: 0, height: 200 },
    enabled: true,
  });

  // ── Keyboard scroll-into-view ─────────────────────────────────────────────
  useEffect(() => {
    if (
      lastScrollWasKeyboard.current &&
      isOpen &&
      highlightedIndex >= 0 &&
      highlightedIndex < scrollableItems.length
    ) {
      rowVirtualizer.scrollToIndex(highlightedIndex, {
        align: "auto",
        behavior: "auto",
      });
      lastScrollWasKeyboard.current = false;
    }
  }, [highlightedIndex, isOpen, rowVirtualizer, scrollableItems.length]);

  // ── Scroll to selected on open ────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && value) {
      const idx = scrollableItems.findIndex(({ node }) => node.id === value.id);
      if (idx >= 0) {
        setTimeout(() => {
          rowVirtualizer.scrollToIndex(idx, { align: "center", behavior: "auto" });
          setHighlightedIndex(idx);
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const debouncedSetSearch = useDebouncedCallback(
    (val: string) => {
      setSearchTerm(val);
      setHighlightedIndex(0);
    },
    350,
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (isOpen) debouncedSetSearch(val);
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setInputValue("");
    setSearchTerm("");
    setHighlightedIndex(0);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setInputValue(value ? getLabel(value) : "");
    setSearchTerm("");
  }, [value, getLabel]);

  const handleSelect = useCallback(
    (item: T) => {
      onChange(item);
      setIsOpen(false);
      setInputValue(getLabel(item));
      setSearchTerm("");
    },
    [onChange, getLabel],
  );

  const handleToggle = useCallback((nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  // ── Click outside ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    function onMouseDown(e: MouseEvent) {
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) {
        handleClose();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, handleClose]);

  // ── Keyboard navigation ───────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        handleOpen();
        e.preventDefault();
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        lastScrollWasKeyboard.current = true;
        setHighlightedIndex((p) => Math.min(p + 1, scrollableItems.length - 1));
        e.preventDefault();
        break;
      case "ArrowUp":
        lastScrollWasKeyboard.current = true;
        setHighlightedIndex((p) => Math.max(p - 1, 0));
        e.preventDefault();
        break;
      case "Enter": {
        const row = scrollableItems[highlightedIndex];
        if (row) {
          const item = itemsById.get(row.node.id);
          if (item) handleSelect(item);
        }
        e.preventDefault();
        break;
      }
      case "Escape":
        handleClose();
        e.preventDefault();
        break;
      case "ArrowRight": {
        const row = scrollableItems[highlightedIndex];
        if (row?.node.children?.length && !expandedIds.has(row.node.id)) {
          handleToggle(row.node.id);
        }
        e.preventDefault();
        break;
      }
      case "ArrowLeft": {
        const row = scrollableItems[highlightedIndex];
        if (row && expandedIds.has(row.node.id)) {
          handleToggle(row.node.id);
        }
        e.preventDefault();
        break;
      }
    }
  };

  // ── Sync display value when selection changes externally ──────────────────
  useEffect(() => {
    if (!isOpen) {
      setInputValue(value ? getLabel(value) : "");
    }
  }, [value, isOpen, getLabel]);

  const displayValue = isOpen
    ? inputValue
    : value
      ? getLabel(value)
      : "";

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderRow = (
    row: (typeof visibleItems)[number],
    index: number,
    isSticky: boolean,
    virtualStyle?: React.CSSProperties,
  ) => {
    const item = itemsById.get(row.node.id);
    if (!item) return null;

    const isSelected = value?.id === item.id;
    const isExpanded = expandedIds.has(row.node.id);
    const hasChildren = !!row.node.children?.length;

    return renderItem({
      node: row.node,
      item,
      style: virtualStyle || {},
      isExpanded,
      isSelected,
      isHighlighted: !isSticky && highlightedIndex === index,
      hasChildren,
      level: row.level,
      onToggle: () => handleToggle(row.node.id),
      onClick: () => handleSelect(item),
    });
  };

  // ── Panel min-width ───────────────────────────────────────────────────────
  const panelMinWidth = minWidth || undefined;

  return (
    <Root ref={rootRef} className={className}>
      <Trigger $disabled={disabled} data-disabled={disabled || undefined}>
        <TriggerInput
          ref={inputRef}
          value={displayValue}
          onChange={handleInputChange}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        <ToggleButton
          type="button"
          tabIndex={-1}
          aria-label={isOpen ? "Close" : "Open"}
          onClick={(e) => {
            e.stopPropagation();
            if (isOpen) {
              handleClose();
            } else {
              handleOpen();
              inputRef.current?.focus();
            }
          }}
        >
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </ToggleButton>
      </Trigger>

      {isOpen && (stickyItems.length > 0 || scrollableItems.length > 0) && (
        <Panel
          $maxHeight={maxHeight}
          style={{ minWidth: panelMinWidth }}
          role="listbox"
        >
          {/* Sticky items at the top */}
          {stickyItems.map(({ node, level }, i) => (
            <div
              key={`sticky-${node.id}`}
              style={{ position: "sticky", top: 0, zIndex: zIndex.dropdown }}
              onMouseEnter={() => setHighlightedIndex(-1)}
            >
              {renderRow({ node, level }, i, true)}
            </div>
          ))}

          {/* Virtualized scrollable list */}
          <ScrollArea ref={listRef}>
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
                background: surface.raised,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((vRow) => {
                const row = scrollableItems[vRow.index];
                return (
                  <div
                    key={vRow.key}
                    data-index={vRow.index}
                    ref={(el) => rowVirtualizer.measureElement(el)}
                    onMouseEnter={() => setHighlightedIndex(vRow.index)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${vRow.start}px)`,
                    }}
                  >
                    {renderRow(row, vRow.index, false)}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </Panel>
      )}
    </Root>
  );
}

const TreeSelect = memo(TreeSelectInner) as typeof TreeSelectInner;

export default TreeSelect;
